// eslint-disable-next-line no-unused-vars
import React, { memo, useMemo, useEffect, useState } from 'react';
import { createContext } from 'use-context-selector';
import { node } from 'prop-types';
import useSWR, { useSWRConfig } from 'swr';
import { dequal } from 'dequal';
import memoize from 'micro-memoize';
import noop from 'lodash/noop';
import { stringify } from 'query-string';

import { API_CATEGORIES } from 'Constants';
import CategoriesPropType from 'Types/categories';
import useUser from 'Hooks/useUser';
import useRouter from 'Hooks/useRouter';
import useBreadcrumbs from 'Hooks/useBreadcrumbs';

/**
 * Finds category tree path for any given at any level
 * @param {array }categories
 * @param {number} activeCategoryId
 * @returns { array }
 */
const findActiveCategoryParts = memoize((categories, activeCategoryId) => {
  let found = false;
  const accumulator = [];
  const catLookup = (cats, acc) => {
    if (found) return acc;
    // eslint-disable-next-line no-restricted-syntax
    for (const category of cats) {
      if (found) return acc;
      if (category.id === activeCategoryId) {
        acc.push(category);
        found = true;
        break;
      }
      if (category?.children?.length) {
        acc.push(category);
        catLookup(category.children, acc);
        if (!found) {
          acc.pop();
        }
      }
    }
    return acc;
  };

  return catLookup(categories || [], accumulator);
}, { isEqual: dequal, maxSize: 4 });

export const CategoriesContext = createContext({
  data: null,
  error: undefined,
  categoryHistory: null,
  activeCategory: null,
  activeCategoryParts: null,
  findActiveCategoryParts: noop,
});

const regExp = /^\/products\/[\w+?!\_\,\&\-'\"+\=]*-c(\d+)[\/c\/]?/; // eslint-disable-line

const CategoriesProvider = memo(({ children, categories: initialCategories }) => {
  const { data: user } = useUser();
  const { cache } = useSWRConfig();
  const { location, query, push } = useRouter();
  const [categories, setCategories] = useState(cache.get('categories'));
  const [categoryHistory, setHistory] = useState([]);
  const [activeCategoryParts, setActiveCategoryParts] = useState([]);
  const [activeCategory, setActiveCategory] = useState({});
  const breadcrumbs = useBreadcrumbs();
  const response = useSWR(
    // eslint-disable-next-line no-nested-ternary
    user?.loggedIn ? (user?.email ? `${API_CATEGORIES}?uid=${user.email}` : API_CATEGORIES) : null,
    {
      revalidateOnMount: !initialCategories
    }
  );

  useEffect(() => {
    if (
      response?.data
      && !dequal(categories, response.data)
    ) {
      setCategories(response.data);
    }
  }, [response.data]);

  useEffect(() => {
    if (
      user.loggedIn
      && !categories
    ) {
      response.mutate();
    }
  }, [user.loggedIn]);

  useEffect(() => {
    breadcrumbs.setBreadcrumbs(prevState => ({
      ...prevState,
      categories: categories || [],
      activeCategoryParts,
      findActiveCategoryParts
    }));
  }, [
    categories,
    activeCategoryParts
  ]);

  useEffect(() => {
    const categoryMatch = location.pathname.match(regExp);

    if (categoryMatch) {
      const categoryId = (categoryMatch?.length >= 1)
        ? parseInt(categoryMatch.at(1).replace(/^-c/, ''), 10)
        : null;

      if (categoryId) {
        const activeCatParts = findActiveCategoryParts(categories, categoryId);
        if (activeCatParts?.length) {
          setActiveCategoryParts(activeCatParts);
          setActiveCategory(activeCatParts.at(-1));
        }
      }
    } else {
      setActiveCategoryParts([]);
      setActiveCategory({});
    }
  }, [
    categories,
    location.pathname
  ]);

  useEffect(() => {
    if (Object.entries(activeCategory || {}).length) {
      setHistory(prevHistory => {
        if (prevHistory.length >= 2) {
          prevHistory.shift();
        }
        prevHistory.push(activeCategory);
        return prevHistory;
      });
    }
  }, [activeCategory]);

  useEffect(() => {
    const TV_CATEGORY_ID = 1471; // Set default sorting on TV category
    const PHONE_CATEGORY_ID = 1725; // Set default sorting on Phone category
    const TV_DEFAULT_SORT = { order_by: 'total_ordered', order_direction: 'desc' };
    if ((activeCategory.id === TV_CATEGORY_ID || activeCategory.id === PHONE_CATEGORY_ID) && !query.order_by) {
      push({
        pathname: location.pathname,
        search: stringify({
          ...query,
          order_by: TV_DEFAULT_SORT.order_by,
          order_direction: TV_DEFAULT_SORT.order_direction
        })
      });
    }
  }, [activeCategory]);

  const memoizedCategories = useMemo(
    () => ({
      data: categories || [],
      mutate: response.mutate,
      error: response.error,
      categoryHistory,
      activeCategory,
      activeCategoryParts,
      findActiveCategoryParts
    }),
    [categories, response.mutate, response.error, categoryHistory, activeCategory, activeCategoryParts]
  );

  return (
    <CategoriesContext.Provider value={ memoizedCategories }>
      { children }
    </CategoriesContext.Provider>
  );
});

CategoriesProvider.propTypes = {
  children: node,
  categories: CategoriesPropType
};

CategoriesProvider.displayName = 'CategoriesProvider';

export default CategoriesProvider;
